/**
 * Ad Referral Tracking Utility
 * Extracts and manages ad referral parameters from URL query strings
 */

// Common ad referral parameters from various platforms
const AD_REFERRAL_PARAMS = {
  // Google Ads
  gclid: 'google_click_id',
  gclsrc: 'google_click_source', 
  gbraid: 'google_gbraid',
  wbraid: 'google_wbraid',
  gad_source: 'google_ads_source',
  gad_campaignid: 'google_ads_campaign_id',
  
  // Facebook/Meta Ads
  fbclid: 'facebook_click_id',
  
  // Microsoft/Bing Ads
  msclkid: 'microsoft_click_id',
  
  // Twitter Ads
  twclid: 'twitter_click_id',
  
  // Reddit Ads
  rdt_cid: 'reddit_click_id',
  
  // TikTok Ads
  ttclid: 'tiktok_click_id',
  
  // LinkedIn Ads
  li_fat_id: 'linkedin_first_party_ads_tracking_uuid',
  
  // Pinterest Ads
  epik: 'pinterest_enhanced_match_code',
  
  // Snapchat Ads
  sc_click_id: 'snapchat_click_id',
  
  // General UTM parameters
  utm_source: 'utm_source',
  utm_medium: 'utm_medium',
  utm_campaign: 'utm_campaign',
  utm_term: 'utm_term',
  utm_content: 'utm_content',
  
  // Custom referral tracking
  ref: 'referral_code',
  affiliate_id: 'affiliate_id',
  partner: 'partner_code'
};

/**
 * Extract ad referral parameters from URL
 * @param {string} url - The URL to extract parameters from (defaults to current URL)
 * @returns {Object} - Object containing referral data
 */
export const extractAdReferralData = (url = window.location.href) => {
  try {
    const urlObj = new URL(url);
    const referralData = {};
    let hasReferralData = false;

    // Extract each referral parameter if present
    Object.entries(AD_REFERRAL_PARAMS).forEach(([param, friendlyName]) => {
      const value = urlObj.searchParams.get(param);
      if (value) {
        referralData[friendlyName] = value;
        referralData[`original_${param}`] = value; // Keep original param name too
        hasReferralData = true;
      }
    });

    // Add metadata if we found any referral data
    if (hasReferralData) {
      referralData.referral_url = url;
      referralData.referral_timestamp = new Date().toISOString();
      referralData.referrer = document.referrer || 'direct';
    }

    return hasReferralData ? referralData : null;
  } catch (error) {
    console.warn('Failed to extract ad referral data:', error);
    return null;
  }
};

/**
 * Store referral data in session storage for persistence across page navigation
 * @param {Object} referralData - The referral data to store
 */
export const storeReferralData = (referralData) => {
  if (!referralData) return;
  
  try {
    sessionStorage.setItem('ad_referral_data', JSON.stringify(referralData));
  } catch (error) {
    console.warn('Failed to store referral data:', error);
  }
};

/**
 * Retrieve stored referral data from session storage
 * @returns {Object|null} - Stored referral data or null if not found
 */
export const getStoredReferralData = () => {
  try {
    const storedData = sessionStorage.getItem('ad_referral_data');
    return storedData ? JSON.parse(storedData) : null;
  } catch (error) {
    console.warn('Failed to retrieve stored referral data:', error);
    return null;
  }
};

/**
 * Clear stored referral data (useful for testing or privacy)
 */
export const clearReferralData = () => {
  try {
    sessionStorage.removeItem('ad_referral_data');
  } catch (error) {
    console.warn('Failed to clear referral data:', error);
  }
};

/**
 * Get referral data for the current session (checks URL first, then storage)
 * @returns {Object|null} - Current referral data or null
 */
export const getCurrentReferralData = () => {
  // First check if there's fresh referral data in the current URL
  const freshData = extractAdReferralData();
  if (freshData) {
    storeReferralData(freshData);
    return freshData;
  }
  
  // If no fresh data, return stored data from this session
  return getStoredReferralData();
};
